import { Ball, SimulationConfig, EscapeRing, ShapeType } from '@/types';
import { IRenderer } from './IRenderer';
import { WebGLContext } from './webgl/WebGLContext';
import { BallRenderer } from './webgl/BallRenderer';
import { RingRenderer } from './webgl/RingRenderer';
import { BlastRenderer } from './webgl/BlastRenderer';
import { FXRenderer } from './webgl/FXRenderer';
import { TrailRenderer } from './webgl/TrailRenderer';
import { LineRenderer } from './webgl/LineRenderer';
import { GPUParticleSystem } from './webgl/GPUParticleSystem';
import { GPUPingPongTrails } from './webgl/GPUPingPongTrails';
import { WebGLUtils } from './webgl/WebGLUtils';
import { FrameScheduler } from '@/services/FrameScheduler';

export class WebGLRenderer implements IRenderer {
  private context: WebGLContext;
  private ballRenderer: BallRenderer;
  private ringRenderer: RingRenderer;
  private blastRenderer: BlastRenderer;
  private fxRenderer: FXRenderer;
  private trailRenderer: TrailRenderer;
  private lineRenderer: LineRenderer;
  private gpuParticleSystem: GPUParticleSystem;
  private gpuPingPongTrails: GPUPingPongTrails;

  private lastTime = Date.now();
  private processedCollisionIds = new Set<string>();

  constructor(canvas: HTMLCanvasElement) {
    this.context = new WebGLContext(canvas);
    this.ballRenderer = new BallRenderer(this.context);
    this.ringRenderer = new RingRenderer(this.context);
    this.blastRenderer = new BlastRenderer(this.context);
    this.fxRenderer = new FXRenderer(this.context);
    this.trailRenderer = new TrailRenderer(this.context);
    this.lineRenderer = new LineRenderer(this.context);
    this.gpuParticleSystem = new GPUParticleSystem(this.context);
    this.gpuPingPongTrails = new GPUPingPongTrails(this.context);
  }

  resize(width: number, height: number) {
    this.context.resize(width, height);
    this.gpuPingPongTrails.resize(width, height);
  }

  clear() {
    this.context.clear();
  }

  render(balls: Ball[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel: number = 1.0) {
    this.ballRenderer.render(balls, config, camera, zoomLevel);
  }

  renderBallPreviews(previews: { pos: {x:number, y:number}, vel: {x:number, y:number}, radius: number, color: string }[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel: number = 1.0) {
    const balls: Ball[] = previews.map((p, i) => ({
      id: `preview-${i}`,
      pos: p.pos,
      vel: p.vel,
      radius: p.radius,
      color: p.color,
      mass: 1,
      angle: Math.atan2(p.vel.y, p.vel.x),
      style: config.ballStyle
    }));

    // Render ghost balls
    this.ballRenderer.render(balls, { ...config, ballFillOpacity: 0.3, ballStrokeOpacity: 0.3 }, camera, zoomLevel);

    // Render direction arrows
    const arrowColor = WebGLUtils.hexToRgb(config.darkMode ? '#ffffff' : '#000000');
    balls.forEach(b => {
      const angle = Math.atan2(b.vel.y, b.vel.x);
      const length = 40; // Arrow length
      this.lineRenderer.renderArrow(b.pos, angle, length, arrowColor, 0.6, camera, zoomLevel);
    });
  }

  renderBlasts(blasts: any[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel: number = 1.0) {
    this.blastRenderer.render(blasts, config, camera, zoomLevel);
  }

  renderFX(events: any[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel: number = 1.0) {
    this.fxRenderer.render(events, config, camera, zoomLevel);
  }
  
  renderTrails(trailCircles: any[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel: number = 1.0) {
    this.trailRenderer.render(trailCircles, config, camera, zoomLevel);
  }

  renderRings(rings: EscapeRing[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, morph: { from: ShapeType, to: ShapeType, factor: number }, globalHue: number, zoomLevel: number = 1.0) {
    this.ringRenderer.render(rings, config, camera, morph, globalHue, zoomLevel);
  }
  
  renderContainer(config: SimulationConfig, camera: { x: number, y: number, scale: number }, morph: { from: ShapeType, to: ShapeType, factor: number }, radius: number, hue: number, zoomLevel: number = 1.0) {
    this.ringRenderer.renderContainer(config, camera, morph, radius, hue, zoomLevel);
  }

  // --- New GPU Phase 6 Core Computes ---
  
  updateCollisionEvents(collisionEvents: any[], config: SimulationConfig) {
    if (config.gpuParticlesEnabled === false) return;

    const maxSavedIds = 2000;
    collisionEvents.forEach(e => {
      if (!this.processedCollisionIds.has(e.id)) {
        this.processedCollisionIds.add(e.id);
        
        if (this.processedCollisionIds.size > maxSavedIds) {
          const it = this.processedCollisionIds.values().next();
          if (!it.done) this.processedCollisionIds.delete(it.value);
        }

        const count = Math.max(1, Math.round((config.collisionEffectParticleCount || 20) * FrameScheduler.getLODScale()));
        const speed = config.collisionEffectGravity !== undefined ? 1.0 : 0.8;
        this.gpuParticleSystem.emit(e.pos.x, e.pos.y, e.color, count, speed, e.duration || 800);
      }
    });
  }

  renderGPUParticles(config: SimulationConfig, camera: { x: number, y: number, scale: number }) {
    const now = Date.now();
    const dt = Math.min(33.3, now - this.lastTime);
    this.lastTime = now;

    this.gpuParticleSystem.updateAndRender(config, camera, dt);
  }

  prepareGPUPingPongTrails(config: SimulationConfig) {
    this.gpuPingPongTrails.renderPulseAndDecay(config);
  }

  renderBallsToTrails(balls: Ball[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel: number = 1.0) {
    this.gpuPingPongTrails.bindTarget();
    // Render the ball shapes into our current trails frame buffer texture
    this.ballRenderer.render(balls, { ...config, ballFillOpacity: 1.0 } as any, camera, zoomLevel);
  }

  drawGPUTrailsToScreen() {
    this.gpuPingPongTrails.drawToScreen();
  }

  resetGPUPasses() {
    this.gpuParticleSystem.reset();
    this.gpuPingPongTrails.reset();
    this.processedCollisionIds.clear();
    this.lastTime = Date.now();
  }

  dispose() {
    this.context.dispose();
  }
}
